import React, { useEffect, useRef, useState } from "react";
import AgGridWithColumnState from "../components/AgGridWithColumnState";
import axios from "axios";
import { message } from "antd";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { makePriorityList } from "../../core/api/mediaplan";
import { useDispatch, useSelector } from "react-redux";

const MarketingPriorityList = () => {
  const gridRef = useRef(null);
  const [fullData, setFullData] = useState([]);
  const [visibleData, setVisibleData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5000);
  const [loading, setLoading] = useState(false);
  const selectedClients = useSelector((state) => state.selectedClients);
  const data = useSelector((state) => state.toggle_header);
  console.log("Selected Clients:", selectedClients);

  const fetchPriorityData = async () => {
    setLoading(true);

    // Clear previous data immediately (to reset UI)
    setFullData([]);
    setVisibleData([]);

    try {
      // Prefer localStorage if available
      let clientIds = [];
      const stored = localStorage.getItem("selectedClientIds");
      const parsed = stored ? JSON.parse(stored) : [];

      if (Array.isArray(parsed) && parsed.length > 0) {
        clientIds = parsed;
      } else if (Array.isArray(selectedClients) && selectedClients.length > 0) {
        clientIds = selectedClients.map((client) =>
          typeof client === "object" && client !== null ? client.id : client
        );
      }

      if (clientIds.length === 0) {
        console.warn("No client IDs available");
        return;
      }

      const response = await makePriorityList(clientIds);
      const results = response.data?.results || response.data || [];

      const indexedData = results.map((item, index) => ({
        ...item,
        index: index + 1,
      }));

      setFullData(indexedData);
      setPage(1);
    } catch (error) {
      console.error("Failed to fetch marketing priority data:", error);
      message.error("Failed to fetch marketing priority data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPriorityData();
  }, [selectedClients]);

  useEffect(() => {
    const interval = setInterval(() => {
      const noRowsEl = document.querySelector(".ag-overlay-no-rows-wrapper");

      if (loading && noRowsEl) {
        noRowsEl.style.display = "none";
      } else if (!loading && noRowsEl) {
        noRowsEl.style.display = "";
      }
    }, 100); // Keep checking for DOM element (Ag Grid renders it async)

    return () => clearInterval(interval); // Cleanup
  }, [loading]);

  useEffect(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    setVisibleData(fullData.slice(start, end));
  }, [fullData, page, pageSize]);

  const totalItems = fullData.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (newPage, newSize = pageSize) => {
    setPageSize(newSize);
    setPage(newPage);
  };
  const uniqueValues = (data, field) => {
    if (!Array.isArray(data)) return [];

    return [...new Set(data.map((item) => field.split(".").reduce((obj, key) => obj?.[key], item)))]
      .filter(Boolean)
      .map((value) => ({ text: value, value }));
  };

  const columnDefs = [
    { headerName: "#", field: "index", width: 60 },
    {
      headerName: "Brand",
      field: "brand",
      width: 150,
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        values: [...new Set(fullData.map((item) => item.brand).filter(Boolean))],
        suppressMiniFilter: false,
      },
    },

    { headerName: "Mail Date", field: "mail_date", width: 130 },
    { headerName: "Product Name", field: "product_name", width: 180 },
    { headerName: "Strategy", field: "strategy", width: 120 },
    { headerName: "Category", field: "category", width: 140 },
    {
      headerName: "Confirmed",
      field: "confirmed",
      width: 120,
      cellRenderer: (params) => (params.value ? "Confirmed" : "Not Confirmed"),
    },
    { headerName: "Campaign Code", field: "campaign_code", width: 150 },
    { headerName: "Printer", field: "printer", width: 120 },
    { headerName: "Offer Code", field: "offer_code", width: 150 },
    { headerName: "Target Qty", field: "target_mail_qty", width: 120 },
    { headerName: "Actual Qty", field: "actual_mail_qty", width: 120 },
    {
      headerName: "Priority",
      field: "priority",
      width: 180,
      pinned: "right",
      cellStyle: (params) => {
        const value = params.value?.toLowerCase();
        if (value?.includes("high")) {
          return {
            backgroundColor: "#f8d7da",
            color: "#721c24",
            fontWeight: "bold",
          }; // light red
        } else if (value?.includes("medium")) {
          return {
            backgroundColor: "#fff3cd",
            color: "#856404",
            fontWeight: "bold",
          }; // light yellow
        } else if (value?.includes("low")) {
          return { backgroundColor: "#d4edda", color: "#155724" }; // light green
        }
        return {};
      },
    },
    { headerName: "Total Print", field: "totalprint", width: 120 },
    { headerName: "Qty Available", field: "quantity_available", width: 150 },
    {
      headerName: "Reason",
      field: "reason",
      width: 240,
      cellRenderer: (params) => {
        const reason = params.value;
        const qtyStatus = params.data.quantity_status;
        const needsMore = params.data.needs_more_stock;
        const qtyAvailable = params.data.quantity_available;

        if (!reason && !qtyStatus && !needsMore) return null;

        return (
          <OverlayTrigger
            trigger="click"
            rootClose
            placement="left"
            overlay={
              <Tooltip id={`tooltip-reason-${params.rowIndex}`}>
                <div style={{ padding: "8px", maxWidth: "300px" }}>
                  <p>
                    <strong>Qty Status:</strong> {qtyStatus}
                  </p>
                  <p>
                    {/* <strong>Needs More Qty Available?</strong>{" "} */}
                    <span
                      style={{
                        color: needsMore ? "red" : "green",
                        fontWeight: "bold",
                      }}>
                      {needsMore ? `Yes (Shortage: ${qtyAvailable || 0})` : "No"}
                    </span>
                  </p>
                  <p>
                    <strong>Reason:</strong> {reason || "N/A"}
                  </p>
                </div>
              </Tooltip>
            }>
            <button className="btn btn-xs btn-outline-primary">View Reason</button>
          </OverlayTrigger>
        );
      },
    },
  ];

  return (
    <div className="container-fluid mt-5">
      <div className="ag-theme-quartz" style={{ height: 600, width: "100%" }}>
        <AgGridWithColumnState
          tableId="marketing-priority-table"
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={visibleData}
          defaultColDef={{ resizable: true, sortable: true, filter: true }}
          suppressPaginationPanel={true}
          pagination={false}
        />
      </div>
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div style={{ marginTop: "15px", fontWeight: "bold" }}>Loading marketing Priority...</div>
        </div>
      )}
      {/* Pagination UI */}
      <div className="row mt-3">
        <div className="col-md-6">
          <div className="pagination-info">
            Showing {totalItems > 0 ? (page - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(page * pageSize, totalItems)} of {totalItems} entries
          </div>
        </div>
        <div className="col-md-6">
          <div className="d-flex justify-content-end gap-2 flex-wrap">
            <div className="d-flex align-items-center me-3">
              <span className="me-2">Show</span>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={(e) => handlePageChange(1, parseInt(e.target.value))}>
                {[10, 25, 50, 100, 500, 1000, 2000, 5000].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="ms-2">entries</span>
            </div>

            <div className="d-flex gap-1 flex-wrap">
              <button
                onClick={() => handlePageChange(1)}
                disabled={page === 1}
                className="btn btn-sm btn-outline-secondary">
                First
              </button>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="btn btn-sm btn-outline-secondary">
                Prev
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${
                      page === pageNum ? "btn-primary" : "btn-outline-secondary"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="btn btn-sm btn-outline-secondary">
                Next
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={page === totalPages}
                className="btn btn-sm btn-outline-secondary">
                Last
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingPriorityList;
